<template>
  <div class="header-bottom">
    <div class="container">
      <AppLink class="logo-link" to="/">
        <img
          src="~/assets/images/logotypes/yavagro-full.svg"
          alt="Logotype"
          width="165"
          height="47"
        />
      </AppLink>

      <nav class="navigation">
        <ul class="list">
          <li v-for="(item, idx) in headerMenuBottom" :key="idx" class="item">
            <AppLink :to="item.url">{{ item.name }}</AppLink>
          </li>
        </ul>
      </nav>

      <div class="search-wrapper">
        <BaseSearchWithButton :value="query" @update:value="setQuery" @on-search="submitSearch" />

        <button class="menu-button" type="button" aria-label="Открыть меню" @click="openSideMenu">
          <BaseIconSvoi icon="fluent/list-24-filled" size="m" />
        </button>
      </div>

      <div class="additional-actions">
        <button
          class="button -location"
          type="button"
          aria-label="Выбрать регион"
          @click="openGeoModal"
        >
          <BaseIconSvoi icon="location/location" size="s" />
        </button>

        <button
          class="button"
          type="button"
          aria-label="Личный кабинет"
          @click="openAuthModal"
        >
          <BaseIconSvoi icon="users/user" size="s" />
        </button>

        <client-only>
          <HeaderEcosystem v-if="!isMobile" />
          <template #fallback>
            <div class="ecosystem-placeholder" />
          </template>
        </client-only>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { headerMenuBottom } from '~/shared/const/menu'
  import { HeaderEcosystem } from '~/components/layouts/header/ecosystem'
  import { BaseSearchWithButton } from '~/components/base-ui/searches'
  import { BaseIconSvoi } from '~/components/base-ui/icon'
  import { AppLink } from '~/components/base-ui/link'
  import { useBreakpoint } from '~/composables/use-breakpoint'
  import { useGlobalSearch } from '~/composables/global-search/use-global-search'
  import { useAuthModal } from '~/composables/use-auth-modal'
  import { useUserStore } from '~/store/user'

  const emit = defineEmits(['open-side-menu', 'open-geo-modal'])
  const router = useRouter()
  const { isMobile } = useBreakpoint()
  const { query, setQuery, submitSearch } = useGlobalSearch()
  const { openModal } = useAuthModal()
  const { isAuthenticated } = useUserStore()

  const openSideMenu = () => {
    emit('open-side-menu')
  }

  const openGeoModal = () => {
    emit('open-geo-modal')
  }

  const openAuthModal = () => {
    if (!isAuthenticated) {
      openModal()
      return
    }

    router.push('/account')
  }
</script>

<style lang="scss" scoped>
  .header-bottom {
    display: flex;
    align-items: center;
    padding: 24px 0;
    border-bottom: 1px solid $color-figs-10;
    background: $color-default-white;

    @include vp-1439 {
      box-shadow: 0 4px 25px rgb(0 0 0 / 7%);
    }

    @include vp-767 {
      padding: 14px 0;
    }

    > .container {
      @include container;

      display: grid;
      grid-auto-flow: column;
      align-items: center;

      @include vp-1439 {
        gap: 12px;
      }

      > .logo-link {
        width: fit-content;
      }
    }

    > .container > .navigation {
      @include vp-1439 {
        display: none;
      }
    }

    > .container > .navigation > .list {
      @include list-reset;

      display: flex;
      gap: 24px;

      > .item {
        > a {
          @extend %font-14, %font-weight-500;

          display: inline-block;
          transition: color $trans-default;

          @include hover-focus {
            cursor: pointer;
            color: $color-kiwi-50;
          }
        }

        > .router-link-active {
          border-bottom: 2px solid $color-kiwi-50;
        }
      }
    }

    > .container > .search-wrapper {
      display: flex;
      align-items: center;
      justify-content: space-between;

      @include vp-1439 {
        grid-row: 2;
        grid-column: span 2;
        gap: 16px;
      }

      @include vp-767 {
        display: none;
      }

      > .menu-button {
        display: none;
        align-items: center;
        justify-content: center;
        width: 40px;
        height: 40px;
        border: none;
        background-color: transparent;
        transition: color $trans-default;

        @include vp-1439 {
          display: flex;
        }

        @include hover-focus {
          cursor: pointer;
          color: $color-kiwi-50;
        }
      }
    }

    > .container > .additional-actions {
      display: flex;
      align-items: center;
      gap: 12px;
      margin-left: auto;

      > .button {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 40px;
        height: 40px;
        background-color: $color-default-white;
        border: 2px solid $color-light-gray;
        border-radius: 50%;
        transition: border-color $trans-default;
        box-shadow: 0 4px 20px 0 rgb(0 0 0 / 8%);

        @include hover-focus {
          cursor: pointer;
          border-color: $color-potato-60;
        }

        &.-location {
          display: none;
          color: $color-kiwi-50;
          background-color: $color-kiwi-10;
          border-color: $color-kiwi-10;

          @include vp-1439 {
            display: flex;
          }

          @include hover-focus {
            border-color: $color-kiwi-60;
          }
        }
      }

      > .ecosystem-placeholder {
        width: 40px;
        height: 40px;

        @include vp-767 {
          display: none;
        }
      }
    }
  }
</style>
